import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type {
  CollabEditorConnectionState,
  CollabEditorParticipant,
  CollabEditorState,
} from '@nimbalyst/collab-bundle/editor';

export type BrowserConnectionState =
  | 'local'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'offline'
  | 'read-only';

const connectionLabels: Record<BrowserConnectionState, string> = {
  local: 'Local harness',
  connecting: 'Connecting',
  live: 'Live',
  reconnecting: 'Reconnecting',
  offline: 'Offline',
  'read-only': 'Read-only',
};

/**
 * States that need saying out loud.
 *
 * A connected, writable document is the expected state, so it gets a dot and
 * nothing more — the same reasoning that already removed the permanent
 * "Editing" chip. Everything else constrains what the reader can do and is
 * spelled out next to the dot.
 */
const silentConnections = new Set<BrowserConnectionState>(['live', 'local']);

/**
 * The colour each state paints in, as a Tailwind text utility -- the dot is
 * `currentColor`, so one class covers both it and the word beside it.
 */
const connectionTone: Record<BrowserConnectionState, string> = {
  local: 'text-nim-muted',
  connecting: 'text-nim-muted',
  reconnecting: 'text-nim-muted',
  live: 'text-nim-success',
  offline: 'text-nim-error',
  'read-only': 'text-nim-error',
};

export function presentConnectionState(
  state: CollabEditorState,
  hasBeenLive: boolean,
): BrowserConnectionState {
  if (state.serverAccess === 'read-only' || state.serverAccess === 'revoked') return 'read-only';
  const connection: CollabEditorConnectionState = state.connection;
  if (connection === 'local') return 'local';
  if (connection === 'connected') return 'live';
  if (connection === 'offline-unsynced' || connection === 'error') return 'offline';
  if (connection === 'disconnected') return hasBeenLive ? 'reconnecting' : 'connecting';
  return hasBeenLive ? 'reconnecting' : 'connecting';
}

/**
 * What the chrome around a shared document reports about it.
 *
 * Deliberately no save state. Collaborative edits are streamed and
 * acknowledged continuously; there is no save step to be behind on, and the
 * bundle's `edit` flag — which latches on the first keystroke and clears only
 * on an explicit flush — answers "should this mount flush before it goes away",
 * not "does the reader have unsaved work". Presenting it as the latter told
 * every writer their synced document had unsaved changes for the whole session.
 * Genuine save failures are not silent: they render their own panel.
 */
/**
 * The raw-source escape hatch, when this document has one.
 *
 * Carried on the status rather than on a channel of its own because the control
 * belongs in the Document actions menu the breadcrumb already renders, and that
 * menu reads exactly this object. Absent for every document whose editor does
 * not declare source mode, or whose bundle ships no codec to project it with,
 * so nothing appears in the menu for a document that could not honour it.
 */
export interface CollabDocumentSourceMode {
  active: boolean;
  toggle: () => void;
}

export interface CollabDocumentStatus {
  connection: BrowserConnectionState;
  readOnly: boolean;
  /** Remote participants only — the local writer is never in their own facepile. */
  participants: CollabEditorParticipant[];
  desktopHref?: string;
  sourceMode?: CollabDocumentSourceMode;
}

interface StatusChannel {
  status: CollabDocumentStatus | null;
  publish: (status: CollabDocumentStatus | null) => void;
}

/**
 * Null rather than a no-op default, so a consumer can tell "no one is hosting
 * this status" apart from "the document has not reported yet". The editor mount
 * falls back to rendering its own strip on that answer, which is what keeps the
 * standalone harness routes legible.
 */
const CollabDocumentStatusContext = createContext<StatusChannel | null>(null);

/**
 * Carries a mounted document's status up to the breadcrumb bar.
 *
 * The editor mounts under the router outlet and the breadcrumb is a sibling in
 * the shell's header, so the two never meet through props. This provider wraps
 * both.
 */
export function CollabDocumentStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CollabDocumentStatus | null>(null);
  const publish = useCallback((next: CollabDocumentStatus | null) => setStatus(next), []);
  const channel = useMemo(() => ({ status, publish }), [publish, status]);
  return (
    <CollabDocumentStatusContext.Provider value={channel}>
      {children}
    </CollabDocumentStatusContext.Provider>
  );
}

/** The publish half, or null when nothing is hosting this document's status. */
export function useCollabDocumentStatusSink(): ((status: CollabDocumentStatus | null) => void) | null {
  return useContext(CollabDocumentStatusContext)?.publish ?? null;
}

/** The read half. Null until a document mounts and reports. */
export function useCollabDocumentStatus(): CollabDocumentStatus | null {
  return useContext(CollabDocumentStatusContext)?.status ?? null;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

/** Remote writers currently in the room, matching desktop's header facepile. */
function ParticipantAvatars({ participants }: { participants: CollabEditorParticipant[] }) {
  if (participants.length === 0) return null;
  return (
    <div className="collab-presence-avatars flex items-center" data-testid="collab-header-presence">
      {participants.map(participant => (
        <span
          key={participant.memberId}
          // The ring is the page background, not a border colour: overlapping
          // faces have to cut into each other cleanly.
          className="collab-presence-avatar -ms-1.5 flex size-5 items-center justify-center rounded-full border-[1.5px] border-[color:var(--nim-bg)] text-[0.56rem] font-medium text-white first:ms-0"
          style={{ backgroundColor: participant.cursorColor }}
          title={participant.displayName}
        >
          {initials(participant.displayName)}
        </span>
      ))}
    </div>
  );
}

/**
 * Connectivity and presence for the document on screen.
 *
 * One row, alongside the breadcrumb that names the document — the arrangement
 * the desktop app already uses. The browser used to spend a second full-width
 * strip below the breadcrumb on the same two facts plus a save state that did
 * not exist.
 */
export function CollabDocumentStatusIndicator({ status }: { status: CollabDocumentStatus | null }) {
  if (!status) return null;
  // No access chip beside this. The connection state already answers what the
  // reader may do — "Connecting", then "Live" or "Read-only" — and a second
  // "Checking access" next to "Connecting" only restated it.
  const label = connectionLabels[status.connection];
  const spoken = !silentConnections.has(status.connection);
  return (
    <div className="collab-document-status flex shrink-0 items-center gap-2" data-connection-state={status.connection}>
      {/* One live region covering the dot and its word, so the state is
          announced once whether or not it is spelled out on screen. */}
      <span
        className={`collab-status inline-flex items-center gap-1.5 ${connectionTone[status.connection]}`}
        role="status"
        aria-label={`Document status: ${label}`}
        title={label}
      >
        <span className="collab-status-dot size-[0.45rem] shrink-0 rounded-full bg-current" aria-hidden="true" />
        {spoken && <span className="collab-status-label whitespace-nowrap text-[0.72rem]">{label}</span>}
      </span>
      <ParticipantAvatars participants={status.participants} />
      {/* Text-only on purpose. `MaterialSymbol` renders icon names as ligature
          text with no `aria-hidden` (NIM-2432), so an icon here would fuse into
          the link's accessible name. */}
      {status.desktopHref && (
        <a
          className="collab-editor-desktop-handoff shrink-0 whitespace-nowrap rounded border border-nim px-2.5 py-1 text-[0.72rem] font-semibold text-nim-link no-underline transition-colors duration-150 hover:border-nim-focus hover:bg-nim-hover"
          href={status.desktopHref}
        >
          Open in desktop
        </a>
      )}
    </div>
  );
}
